import {
  IconSquareRoundedCheckFilled,
  IconSquareRoundedPlus,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Card } from '@cfa/react-components';
import { Focusable, Tooltip } from '@cfa/react-core';

import { formatDate } from '@/utils/date';
import { cn } from '@/utils/tailwind';

import { AlreadyAssignedMd, Ribbon } from '@/icons';

export type TeamMemberPlanStatus = 'unassigned' | 'in-progress' | 'completed';

interface TeamMemberCardProps {
  id: string;
  isAssigned: boolean;
  isTrainingMode: boolean;
  name: string;
  onAssignTeamMember: (id: string) => void;
  originallyAssigned: boolean;
  reassignEligible?: boolean;
  simpleUserName: string;
  planStatus?: TeamMemberPlanStatus;
  completionDate?: string;
  noHover?: boolean;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({
  id,
  isAssigned,
  isTrainingMode,
  name,
  onAssignTeamMember,
  originallyAssigned,
  reassignEligible = false,
  simpleUserName,
  planStatus = 'unassigned',
  completionDate,
  noHover,
}) => {
  const { t } = useTranslation();
  const showReassignmentFeatures = !isTrainingMode && reassignEligible;

  const getTooltipText = () => {
    if (planStatus === 'completed')
      return t('TrainingPlans.teamMembers.tooltipReassign');
    if (planStatus === 'in-progress')
      return t('TrainingPlans.teamMembers.tooltipUpdate');
    return t('TrainingPlans.teamMembers.tooltipAssign');
  };

  const formatCompletionDate = (dateString?: string) => {
    if (!dateString) return '';
    return formatDate(dateString, 'en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusTooltipText = () => {
    if (planStatus === 'completed' && completionDate) {
      return t('TrainingPlans.teamMembers.completedOn', {
        date: formatCompletionDate(completionDate),
      });
    }
    if (planStatus === 'in-progress') {
      return t('TrainingPlans.teamMembers.alreadyAssigned');
    }
    return null;
  };

  const renderStatusIcon = () => {
    const statusTooltipText = getStatusTooltipText();

    if (planStatus === 'completed') {
      return (
        <Tooltip.Root delay={200} closeDelay={100}>
          <Focusable>
            <Ribbon className="size-8!" />
          </Focusable>
          <Tooltip.Content className="px-2! py-1! text-xs" placement="left">
            {statusTooltipText}
          </Tooltip.Content>
        </Tooltip.Root>
      );
    }
    if (planStatus === 'in-progress') {
      return (
        <Tooltip.Root delay={200} closeDelay={100}>
          <Focusable>
            <AlreadyAssignedMd className="size-8!" />
          </Focusable>
          <Tooltip.Content className="px-2! py-1! text-xs" placement="left">
            {statusTooltipText}
          </Tooltip.Content>
        </Tooltip.Root>
      );
    }
    return null;
  };

  const renderActionIcon = () => {
    if (originallyAssigned && isTrainingMode) {
      return (
        <div className="flex items-center gap-2">
          <Ribbon className="size-8!" />
          <IconSquareRoundedCheckFilled className="text-grayscale-gray2! size-8!" />
        </div>
      );
    }

    if (planStatus !== 'unassigned' && showReassignmentFeatures) {
      return (
        <div className="flex items-center gap-2">
          {renderStatusIcon()}
          <Tooltip.Root delay={200} closeDelay={100}>
            <Focusable>
              {isAssigned ? (
                <IconSquareRoundedCheckFilled
                  data-testid="AddTeamMember"
                  onClick={() => onAssignTeamMember(id)}
                  className="text-feedback-success-base! size-8! cursor-pointer!"
                />
              ) : (
                <IconSquareRoundedPlus
                  data-testid="AddTeamMember"
                  onClick={() => onAssignTeamMember(id)}
                  className="text-brand-navy! size-8! cursor-pointer!"
                />
              )}
            </Focusable>
            <Tooltip.Content className="px-2! py-1! text-xs" placement="bottom">
              {getTooltipText()}
            </Tooltip.Content>
          </Tooltip.Root>
        </div>
      );
    }

    if (originallyAssigned) {
      return (
        <IconSquareRoundedCheckFilled className="text-grayscale-gray2! size-8!" />
      );
    }

    if (isAssigned) {
      return (
        <Tooltip.Root delay={200} closeDelay={100}>
          <Focusable>
            <IconSquareRoundedCheckFilled
              data-testid="AddTeamMember"
              onClick={() => onAssignTeamMember(id)}
              className="text-feedback-success-base! size-8! cursor-pointer!"
            />
          </Focusable>
          <Tooltip.Content className="px-2! py-1! text-xs" placement="bottom">
            {getTooltipText()}
          </Tooltip.Content>
        </Tooltip.Root>
      );
    }

    return (
      <Tooltip.Root delay={200} closeDelay={100}>
        <Focusable>
          <IconSquareRoundedPlus
            data-testid="AddTeamMember"
            onClick={() => onAssignTeamMember(id)}
            className="text-brand-navy! size-8! cursor-pointer!"
          />
        </Focusable>
        <Tooltip.Content className="px-2! py-1! text-xs" placement="bottom">
          {getTooltipText()}
        </Tooltip.Content>
      </Tooltip.Root>
    );
  };

  return (
    <div className="mb-4" data-simpleusername={simpleUserName}>
      <Card
        className={cn('flex! flex-row! items-center! justify-between! p-4!', {
          'no-hover': noHover,
        })}
      >
        <span className="truncate pr-2">{name}</span>
        {renderActionIcon()}
      </Card>
    </div>
  );
};

export default TeamMemberCard;
